import notification from 'helpers/notification';
import classNames from 'classnames';
import api from 'helpers/api';
import contactType from 'helpers/contact_type';
import currencyFormatter from 'helpers/currency_formatter';
import dateFormatter from 'helpers/date_formatter';
import { useAngularService } from 'helpers/get_angular_service';
import redirect from 'helpers/redirect';
import sources from 'helpers/sources';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import Input from '../../components/input';
import ErrorMessage from '../../components/error_message';
import { Gym } from '../../types/gym';
import toFixed from 'helpers/to_fixed';
import Link from 'react/components/link';

interface ContactVoucher {
  id: number;
  name_for_display: string;
  credit_voucher_category_name: string;
  current_balance: string;
  voucher_reference?: string;
  expires_on?: string;
  contact_id?: number;
  contact_name: string;
  contact_type: string;
  contact_status: string;
  contact_archived: boolean;
}

interface SelectedVoucher {
  id: number;
  amount: number;
  contact_id?: number;
  contact_name?: string;
  contact_type?: string;
  contact_archived?: boolean;
}

const ApplyCreditModal = ({ modalInstance, invoice }) => {
  const $rootScope = useAngularService('$rootScope');
  const EmploymentService = useAngularService('EmploymentService');
  const permissions = EmploymentService.get().permissions;
  const gym: Gym = $rootScope.gym;
  const [contactVouchers, setContactVouchers] = useState<undefined | ContactVoucher[]>();
  const [selectedVouchers, setSelectedVouchers] = useState<SelectedVoucher[]>([]);
  const [activeVoucher, setActiveVoucher] = useState<undefined | ContactVoucher>();
  const [activeVoucherAmount, setActiveVoucherAmount] = useState<undefined | string | number>();
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<undefined | string[]>();

  const initialInvoiceAvailableAmount = toFixed(
    invoice.amount - (invoice.credit_applied_amount || 0),
  );

  useEffect(() => {
    const url = sources.contactFamilyCreditVouchers({
      gymId: gym.id,
      contactType: contactType(invoice.contact_type),
      contactId: invoice.contact_id,
    });
    api.get(url, { count: 100 }).then((response) => {
      setContactVouchers(response);
    });
  }, []);

  useEffect(() => {
    if (!contactVouchers) return;
    const items = contactVouchers
      .filter((item) => !selectedVouchers.find((voucher) => voucher.id === item.id))
      .map((item) => {
        const expiredLabel = isVoucherExpired(true) ? '(Expired)' : '';
        const balance = currencyFormatter(item.current_balance, gym.currency_symbol);
        const name = `${item.credit_voucher_category_name}: ${balance} remaining - ${item.name_for_display} ${expiredLabel}`;

        return { id: item.id, name };
      });
    setVoucherOptions(items);
  }, [selectedVouchers, contactVouchers]);

  useEffect(() => {
    if (contactVouchers?.length === 1) {
      onVoucherSelect(contactVouchers[0].id);
    }
  }, [contactVouchers]);

  const appliedAmount = useMemo(() => {
    return selectedVouchers.reduce((acc, item) => {
      return toFixed(acc + item.amount);
    }, 0);
  }, [selectedVouchers]);

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  const addCredit = () => {
    redirect('gym.contacts.edit', {
      contactType: contactType(invoice.contact_type),
      contactId: invoice.contact_id,
    }).then(() => {
      $rootScope.$emit('contact:addCreditVoucher');
    });
    cancel();
  };

  const onVoucherSelect = (id) => {
    const voucher = contactVouchers.find((item) => item.id === id);
    setActiveVoucher(voucher);
    const invoiceAvailableAmount = toFixed(initialInvoiceAvailableAmount - appliedAmount);

    const defaultAmount =
      invoiceAvailableAmount > +voucher.current_balance
        ? voucher.current_balance
        : invoiceAvailableAmount;
    setActiveVoucherAmount(defaultAmount);
  };

  const clearActiveVoucher = () => {
    setActiveVoucher(undefined);
    setActiveVoucherAmount(undefined);
    setErrorMessage(undefined);
  };

  const amountValid = () => {
    return (
      +activeVoucherAmount > 0 &&
      activeVoucherAmount <= activeVoucher.current_balance &&
      toFixed(+activeVoucherAmount + appliedAmount) <= initialInvoiceAvailableAmount
    );
  };

  const applyVoucher = () => {
    setSelectedVouchers((items) =>
      items.concat([
        {
          id: activeVoucher.id,
          amount: +activeVoucherAmount,
          contact_id: activeVoucher.contact_id,
          contact_name: activeVoucher.contact_name,
          contact_type: activeVoucher.contact_type,
          contact_archived: activeVoucher.contact_archived,
        },
      ]),
    );
    clearActiveVoucher();
  };

  const getVoucherById = (id) => {
    return contactVouchers.find((item) => item.id === id);
  };

  const appliedVoucherTitle = (id) => {
    const voucher = getVoucherById(id);
    const ref = voucher.voucher_reference ? `(Ref: ${voucher.voucher_reference})` : '';
    return `${voucher.credit_voucher_category_name} ${ref}`;
  };

  const undoVoucher = (id) => {
    setSelectedVouchers((items) => items.filter((voucher) => voucher.id !== id));
  };

  const isVoucherExpired = (voucher) => {
    return voucher.expires_on && moment(voucher.expires_on).isBefore(moment(), 'days');
  };

  const formReady = () => {
    return !isLoading && selectedVouchers.length;
  };

  const applyCredit = () => {
    if (activeVoucher) {
      setErrorMessage(['Please confirm before continuing.']);
      return;
    }

    const url = `/gyms/${gym.id}/credit_voucher_activities/apply_credit`;
    const params = {
      contact_id: invoice.contact_id,
      redemption_target_type: 'Invoice',
      redemption_target_id: invoice.id,
      credit_vouchers: selectedVouchers,
    };
    setIsLoading(true);
    api.post(url, { credit_voucher_activity: params }).then(
      () => {
        modalInstance.close();
      },
      (response) => {
        setIsLoading(false);
        notification.error(response.data.errors.general || 'Please fix the errors below');
      },
    );
  };

  return (
    <div>
      <div className="modal-header">
        <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
          <i className="icon-cross"></i>
        </a>
        <h4 className="modal-title">Apply credit to payment</h4>
      </div>

      {contactVouchers ? (
        <>
          {contactVouchers.length ? (
            <>
              <div className="modal-body modal-body-visible">
                <div className="margin-bottom-16">
                  <strong>Invoice {invoice.invoice_number_base}: </strong>
                  {invoice.membership_label}
                  <br />
                  {currencyFormatter(invoice.amount, gym.currency_symbol)}, due{' '}
                  {dateFormatter(invoice.due_date, 'DD-MMM-YYYY')}
                </div>
                <div className="margin-bottom-16">
                  Credit applied previously:{' '}
                  <strong>
                    {currencyFormatter(invoice.credit_applied_amount || 0, gym.currency_symbol)}
                  </strong>
                </div>
                <div className="form-group">
                  <label className="control-label">Choose voucher(s) to apply credit from:</label>
                  {selectedVouchers.map((item) => (
                    <div key={item.id} className="form-group">
                      Voucher: {appliedVoucherTitle(item.id)}
                      <br />
                      {item.contact_id !== invoice.contact_id && (
                        <div>
                          {!item.contact_archived ? (
                            <>
                              Voucher belongs to:{' '}
                              <Link
                                route="gym.contacts.edit"
                                target="_blank"
                                routeParams={{
                                  contactId: item.contact_id,
                                  contactType: contactType(item.contact_type),
                                  tab: 'credit_balance',
                                }}
                              >
                                {item.contact_name}
                              </Link>
                            </>
                          ) : (
                            `Voucher belongs to: ${item.contact_name}`
                          )}
                        </div>
                      )}
                      Available balance:{' '}
                      {currencyFormatter(
                        getVoucherById(item.id).current_balance,
                        gym.currency_symbol,
                      )}
                      <br />
                      Amount to apply: {currencyFormatter(item.amount, gym.currency_symbol)}{' '}
                      <Button
                        className="inline-block margin-left"
                        size="xs"
                        variant="primary"
                        onClick={() => undoVoucher(item.id)}
                      >
                        Undo
                      </Button>
                    </div>
                  ))}

                  <div>
                    {activeVoucher ? (
                      <div>
                        <div>
                          Voucher: {activeVoucher.credit_voucher_category_name}{' '}
                          {activeVoucher.name_for_display}
                        </div>
                        {activeVoucher.contact_id !== invoice.contact_id && (
                          <div>
                            {!activeVoucher.contact_archived ? (
                              <>
                                Voucher belongs to:{' '}
                                <Link
                                  route="gym.contacts.edit"
                                  target="_blank"
                                  routeParams={{
                                    contactId: activeVoucher.contact_id,
                                    contactType: contactType(activeVoucher.contact_type),
                                    tab: 'credit_balance',
                                  }}
                                >
                                  {activeVoucher.contact_name}
                                </Link>
                              </>
                            ) : (
                              `Voucher belongs to: ${activeVoucher.contact_name}`
                            )}
                          </div>
                        )}
                        <div>
                          Available balance:{' '}
                          {currencyFormatter(activeVoucher.current_balance, gym.currency_symbol)}
                        </div>
                        {isVoucherExpired(activeVoucher) && (
                          <div>
                            <strong>
                              Note: this credit voucher EXPIRED on{' '}
                              {moment(activeVoucher.expires_on).format('ll')}. You may still choose
                              to use it, however.
                            </strong>
                          </div>
                        )}
                        <div className="margin-top-16">
                          Amount to apply:
                          <div className="margin-left inline-block">
                            <Input
                              type="number"
                              size="sm"
                              value={activeVoucherAmount}
                              onChange={(amount) => setActiveVoucherAmount(amount)}
                            />{' '}
                            <Button
                              className="inline"
                              variant="primary"
                              size="sm"
                              onClick={() => applyVoucher()}
                              disabled={!amountValid()}
                            >
                              Confirm
                            </Button>{' '}
                            <Button
                              className="inline"
                              size="sm"
                              onClick={() => clearActiveVoucher()}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      voucherOptions.length > 0 &&
                      appliedAmount < initialInvoiceAvailableAmount && (
                        <>
                          {selectedVouchers.length > 0 && (
                            <label className="control-label">Add another:</label>
                          )}
                          <span className={classNames({ 'margin-left': selectedVouchers.length })}>
                            <Dropdown
                              options={voucherOptions}
                              onSelect={onVoucherSelect}
                              placeholder="Select credit voucher"
                            />
                          </span>
                        </>
                      )
                    )}
                  </div>
                </div>

                <ErrorMessage error={errorMessage} />

                {selectedVouchers.length > 0 && (
                  <div>
                    <strong>
                      {currencyFormatter(appliedAmount, gym.currency_symbol)} credit will be applied
                      to the selected payment.
                      <br />
                      {appliedAmount === initialInvoiceAvailableAmount
                        ? 'The process will mark the payment as Paid.'
                        : `The payment's remaining balance (chargeable to the customer) will be ${currencyFormatter(
                            toFixed(initialInvoiceAvailableAmount - appliedAmount),
                            gym.currency_symbol,
                          )}`}
                    </strong>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <Button onClick={cancel}>Cancel</Button>
                <Button variant="success" onClick={() => applyCredit()} disabled={!formReady()}>
                  Apply credit now
                </Button>
              </div>
            </>
          ) : (
            <>
              {permissions.manage_credit_vouchers ? (
                <>
                  <div className="modal-body modal-body-visible">
                    <p>
                      This contact currently has no credit available for use. Click the button below
                      to add credit now.
                    </p>
                    <p>
                      Once credit has been added for the contact, return to this payment to apply
                      credit.
                    </p>
                  </div>

                  <div className="modal-footer">
                    <Button onClick={cancel}>Cancel</Button>
                    <Button variant="success" onClick={() => addCredit()}>
                      Add credit to contact
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-body modal-body-visible">
                    Sorry, this contact currently has no credit available for use.
                  </div>

                  <div className="modal-footer">
                    <Button onClick={cancel}>Close</Button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      ) : (
        <div className="modal-body modal-body-visible">Loading...</div>
      )}
    </div>
  );
};

angular.module('gym').value('ApplyCreditModal', ApplyCreditModal);
